<script setup lang="ts">
import { Filter, X } from 'lucide-vue-next'

const activeCategory = ref<string | null>(null)
const activeTag = ref<string | null>(null)
const filtersOpen = ref(false)
const displayCount = ref(8)
const sentinel = ref<HTMLElement | null>(null)
const PAGE_SIZE = 8

// Exclude future-dated (scheduled) posts, newest first.
const today = new Date().toISOString().split('T')[0]

// Local preview: /blog?all=1 lists drafts and scheduled posts too. Dev only, so
// the param does nothing in production.
const route = useRoute()
const showAll = import.meta.dev && route.query.all === '1'

const { data: posts } = await useAsyncData(`blog-list-${showAll ? 'all' : 'live'}`, () => {
  const list = queryCollection('blog').order('date', 'DESC')
  return (showAll ? list : list.where('draft', '=', false).where('date', '<=', today)).all()
})

// Category depth across ALL published posts (live + scheduled), so a category
// that is still dripping out weekly shows a chip from its first live post,
// while a genuine one-off category (e.g. Insights) stays hidden.
const { data: allCats } = await useAsyncData('blog-cat-depth', () =>
  queryCollection('blog').where('draft', '=', false).select('category').all(),
)

const CATEGORY_ORDER = ['Why Migrate', 'Migration Guides', 'Use Cases', 'Laravel Ecosystem', 'Content Management', 'Insights']
const MIN_PER_CATEGORY = 2
const allCategories = computed(() => {
  // Categories with real depth: 2+ posts total in the collection, live or scheduled.
  const total = new Map<string, number>()
  for (const p of allCats.value ?? []) {
    if (p.category) total.set(p.category, (total.get(p.category) ?? 0) + 1)
  }
  // ...that also have at least one live post, so a chip never filters to nothing.
  const liveCats = new Set((posts.value ?? []).map((p) => p.category).filter(Boolean))
  const present = [...total.entries()]
    .filter(([c, n]) => n >= MIN_PER_CATEGORY && liveCats.has(c))
    .map(([c]) => c)
  const ordered = CATEGORY_ORDER.filter((c) => present.includes(c))
  const extras = present.filter((c) => !CATEGORY_ORDER.includes(c)).sort()
  return [...ordered, ...extras]
})

// Posts in the active category (before the tag filter); drives both the tag
// chip list and the final result, so the two filters compose.
const categoryPosts = computed(() => {
  if (!posts.value) return []
  if (!activeCategory.value) return posts.value
  return posts.value.filter((p) => p.category === activeCategory.value)
})

// Tags available within the current category, so the chips stay relevant.
const availableTags = computed(() => {
  const tags = new Set<string>()
  categoryPosts.value.forEach((p) => p.tags?.forEach((t) => tags.add(t)))
  return Array.from(tags).sort()
})

const filteredPosts = computed(() => {
  if (!activeTag.value) return categoryPosts.value
  return categoryPosts.value.filter((p) => p.tags?.includes(activeTag.value!))
})

const visiblePosts = computed(() => filteredPosts.value.slice(0, displayCount.value))
const hasMore = computed(() => displayCount.value < filteredPosts.value.length)

function selectCategory(category: string | null) {
  activeCategory.value = category
  // Tags differ per category, so clear the tag filter to avoid empty combinations.
  activeTag.value = null
  displayCount.value = PAGE_SIZE
}

function selectTag(tag: string | null) {
  activeTag.value = tag
  displayCount.value = PAGE_SIZE
}

// Lock background scroll while the filter sheet is open.
watch(filtersOpen, (open) => {
  if (import.meta.client) document.documentElement.style.overflow = open ? 'hidden' : ''
})
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') filtersOpen.value = false
}

// Infinite scroll: reveal the next page when the sentinel scrolls into view.
let observer: IntersectionObserver | null = null
onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasMore.value) {
        displayCount.value += PAGE_SIZE
      }
    },
    { rootMargin: '300px' },
  )
  watch(sentinel, (el) => {
    if (el) observer?.observe(el)
  }, { immediate: true })
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  observer?.disconnect()
  window.removeEventListener('keydown', onKeydown)
  if (import.meta.client) document.documentElement.style.overflow = ''
})

// Where the post grid's hairlines cross, re-measured as more posts load.
const grid = ref<HTMLElement>()
const joins = useGridJoins(grid)

const tab = (on: boolean) => [
  'relative -mb-px border-b py-3 text-sm transition-colors',
  on
    ? 'border-blu-500 font-medium text-slate-900 dark:text-white'
    : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-white/55 dark:hover:text-white',
]

function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

useHead({
  title: 'Blog',
  meta: [
    { name: 'description', content: 'The AugmentBLU blog. Guides and notes on Laravel, content management, performance, SEO and web development for UK businesses, starting with legacy CMS migration.' },
    { property: 'og:title', content: 'Blog | AugmentBLU' },
    { property: 'og:description', content: 'Guides and notes on Laravel, content management, performance and web development from the AugmentBLU team in Edinburgh.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: 'https://augmentblu.co.uk/blog' },
    { property: 'og:image', content: 'https://augmentblu.co.uk/og/pages/blog.png' },
    { name: 'twitter:image', content: 'https://augmentblu.co.uk/og/pages/blog.png' },
  ],
  link: [
    { rel: 'canonical', href: 'https://augmentblu.co.uk/blog' },
    { rel: 'alternate', type: 'application/rss+xml', title: 'AugmentBLU Blog', href: 'https://augmentblu.co.uk/rss.xml' },
  ],
})

useBreadcrumbs([
  { name: 'Home', url: 'https://augmentblu.co.uk/' },
  { name: 'Blog' },
])
</script>

<template>
  <main class="min-h-screen">
    <div class="mx-auto max-w-7xl px-6 pt-16 md:px-12 md:pt-24">
      <div class="mb-14 max-w-3xl" data-aos="fade">
        <h1 class="mb-6 text-4xl font-bold text-slate-700 dark:text-white md:text-5xl lg:text-6xl">Blog</h1>
        <p class="max-w-xl text-lg leading-relaxed text-slate-700/80 text-pretty dark:text-white/65 md:text-xl">
          Practical guides and notes on Laravel, content management, performance, SEO and building web applications that last, from our team in Edinburgh.
        </p>
      </div>

      <!-- Filters: the topics as a row of tabs on a hairline, then tags in a
           slide-over sheet. -->
      <div v-if="allCategories.length > 1 || availableTags.length" class="-mx-6 mb-12 flex items-center gap-x-6 overflow-x-auto whitespace-nowrap border-b border-slate-900/10 px-6 [scrollbar-width:none] md:mx-0 md:px-0 dark:border-white/10" data-aos="fade">
        <button type="button" :class="tab(!activeCategory)" @click="selectCategory(null)">All posts</button>
        <button
          v-for="category in allCategories"
          :key="category"
          type="button"
          :class="tab(activeCategory === category)"
          @click="selectCategory(activeCategory === category ? null : category)"
        >
          {{ category }}
        </button>

        <div v-if="availableTags.length" class="ml-auto flex shrink-0 items-center gap-4">
          <button
            v-if="activeTag"
            type="button"
            class="inline-flex items-center gap-1 py-3 text-sm font-medium text-blu-600 dark:text-blu-400"
            :aria-label="`Clear the ${activeTag} tag`"
            @click="selectTag(null)"
          >
            {{ activeTag }}
            <X class="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 py-3 text-sm text-slate-600 transition-colors hover:text-slate-900 dark:text-white/55 dark:hover:text-white"
            @click="filtersOpen = true"
          >
            <Filter class="h-3.5 w-3.5" />
            Tags
          </button>
        </div>
      </div>

      <!-- The posts as an open grid, divided only by hairlines, with a cross
           where they meet. Each cell is the link. -->
      <div v-if="visiblePosts.length" class="relative overflow-hidden">
        <div ref="grid" class="-mb-px -mr-px grid md:grid-cols-2">
          <NuxtLink
            v-for="(post, i) in visiblePosts"
            :key="post.path"
            :to="showAll ? { path: post.path, query: { all: '1' } } : post.path"
            data-aos="fade"
            :data-aos-delay="(i % 2) * 80"
            class="group block border-b border-slate-900/10 py-8 transition-colors hover:bg-blu-500/[0.04] dark:border-white/10 md:border-r md:px-8 md:odd:pl-0 md:even:pr-0 md:hover:bg-transparent"
          >
            <div class="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span class="font-medium text-blu-600 dark:text-blu-400">{{ post.category }}</span>
              <time :datetime="post.date" class="text-slate-500 dark:text-white/50">{{ formatDate(post.date) }}</time>
              <span v-if="showAll && post.draft" class="font-medium text-slate-500">Draft</span>
              <span v-else-if="showAll && post.date > today" class="font-medium text-amber-600 dark:text-amber-400">Scheduled</span>
            </div>
            <h2 class="text-xl font-semibold text-slate-700 transition-colors group-hover:text-blu-600 dark:text-white dark:group-hover:text-blu-400 md:text-2xl">
              {{ post.title }}
            </h2>
            <p class="mt-2 line-clamp-3 leading-relaxed text-slate-700/75 dark:text-white/55">{{ post.description }}</p>
          </NuxtLink>
        </div>
        <GridJoins :joins="joins" />
      </div>

      <!-- Infinite-scroll sentinel -->
      <div v-if="hasMore" ref="sentinel" class="flex justify-center py-12">
        <div class="h-7 w-7 animate-spin rounded-full border-2 border-blu-500 border-t-transparent" aria-hidden="true" />
        <span class="sr-only">Loading more posts</span>
      </div>

      <p v-else-if="(activeCategory || activeTag) && !visiblePosts.length" class="py-12 text-slate-700/75 dark:text-white/50">
        No posts match that filter.
        <button type="button" class="ml-1 font-medium text-blu-600 underline decoration-dotted underline-offset-4 hover:decoration-solid dark:text-blu-400" @click="selectCategory(null)">Show all posts</button>
      </p>

      <p v-else-if="!visiblePosts.length" class="py-12 text-slate-700/75 dark:text-white/50">No posts yet. Check back soon.</p>

      <div class="pb-32" />
    </div>

    <!-- Tag filter sheet (slide-over) -->
    <ClientOnly>
      <Teleport to="body">
        <Transition
          enter-active-class="transition-opacity duration-300"
          leave-active-class="transition-opacity duration-200"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0"
        >
          <div v-if="filtersOpen" class="fixed inset-0 z-[300] bg-black/20" @click="filtersOpen = false" />
        </Transition>

        <Transition
          enter-active-class="transition-transform duration-300 ease-out"
          leave-active-class="transition-transform duration-200 ease-in"
          enter-from-class="translate-x-full"
          leave-to-class="translate-x-full"
        >
          <!-- Frosted glass, as the service quick nav, but the page colour at
               60% rather than 10% over the blur, as it holds a panel of text. -->
          <aside
            v-if="filtersOpen"
            class="fixed top-0 right-0 z-[301] flex h-full w-full max-w-sm flex-col border-l border-slate-900/10 bg-[rgba(249,250,251,0.6)] shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-[rgba(15,15,26,0.6)]"
            role="dialog"
            aria-modal="true"
            aria-label="Filter by tag"
          >
            <div class="flex items-start justify-between px-6 py-5 border-b border-dark-500/10 dark:border-white/10">
              <div>
                <h2 class="text-lg font-semibold text-slate-700 dark:text-white">Filter by tag</h2>
                <p class="text-xs text-slate-500 dark:text-white/50 mt-0.5">{{ activeCategory ? `Within ${activeCategory}` : 'Across all posts' }}</p>
              </div>
              <button type="button" @click="filtersOpen = false" aria-label="Close filters" class="p-2 -mr-2 rounded-lg text-slate-500 dark:text-white/50 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors">
                <X class="w-5 h-5" />
              </button>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-5">
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  @click="selectTag(null)"
                  :class="[
                    'text-sm px-3 py-1.5 rounded-full border transition-colors',
                    !activeTag
                      ? 'bg-blu-500/15 text-blu-900 dark:text-blu-300 border-blu-500/40 font-medium'
                      : 'text-slate-600 dark:text-white/60 border-dark-500/10 dark:border-white/[0.08] hover:border-blu-500/40 hover:text-slate-900 dark:hover:text-white',
                  ]"
                >
                  All tags
                </button>
                <button
                  v-for="tag in availableTags"
                  :key="tag"
                  type="button"
                  @click="selectTag(activeTag === tag ? null : tag)"
                  :class="[
                    'text-sm px-3 py-1.5 rounded-full border transition-colors',
                    activeTag === tag
                      ? 'bg-blu-500/15 text-blu-900 dark:text-blu-300 border-blu-500/40 font-medium'
                      : 'text-slate-600 dark:text-white/60 border-dark-500/10 dark:border-white/[0.08] hover:border-blu-500/40 hover:text-slate-900 dark:hover:text-white',
                  ]"
                >
                  {{ tag }}
                </button>
              </div>
            </div>

            <div class="flex items-center justify-between gap-3 px-6 py-4 border-t border-dark-500/10 dark:border-white/10">
              <button type="button" @click="selectTag(null)" class="text-sm text-slate-500 dark:text-white/50 hover:text-slate-700 dark:hover:text-white transition-colors">Clear</button>
              <button type="button" @click="filtersOpen = false" class="text-sm font-medium px-5 py-2 rounded-full bg-blu-500 text-slate-900 hover:bg-blu-400 transition-colors">
                Show {{ filteredPosts.length }} {{ filteredPosts.length === 1 ? 'post' : 'posts' }}
              </button>
            </div>
          </aside>
        </Transition>
      </Teleport>
    </ClientOnly>
  </main>
</template>
